import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CRMSectionHeader, CRMHeaderLinkButton, TabIcons } from './CRMTabUI';
import { kycService } from '../../services/kycService';
import './CRMKYCOnboardingSection.css';

/**
 * KYC Onboarding embed for the CRM profile's KYC & Compliance tab.
 *
 * Shows this partner's KYC cases (per year), portal progress and review
 * outcome, with one-click navigation to the full case review. Pure CRM
 * styling: crm-form-section card + CRMSectionHeader + crm-status-badge.
 */

const STATUS_META = {
    invited: { label: 'Invited', badge: 'crm-status-pending' },
    in_progress: { label: 'With Partner', badge: 'crm-status-pending' },
    submitted: { label: 'Awaiting Review', badge: 'crm-status-pending' },
    approved: { label: 'Approved', badge: 'crm-status-approved' },
    rejected: { label: 'Rejected', badge: 'crm-status-flagged' },
};

const CRMKYCOnboardingSection = ({ profileId }) => {
    const history = useHistory();
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!profileId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            try {
                const data = await kycService.getCases({ crm_profile: profileId });
                if (!cancelled) setCases(Array.isArray(data) ? data : data.results || []);
            } catch (err) {
                if (!cancelled) setCases([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [profileId]);

    if (!profileId) return null;

    return (
        <div className="crm-form-section crm-kyc-ob-section">
            <CRMSectionHeader
                icon={<TabIcons.UserCheck />}
                iconTone="emerald"
                title="KYC Onboarding Portal"
                count={cases.length || null}
                countLabel={cases.length === 1 ? 'case' : 'cases'}
                actions={(
                    <CRMHeaderLinkButton onClick={() => history.push('/dashboard/crm/kyc')}>
                        Open KYC Dashboard
                    </CRMHeaderLinkButton>
                )}
                help="Partner-completed onboarding cases — one per year. The partner uploads documents and fills the 000 Onboarding form through the secure KYC portal; AI pre-fills, compliance reviews here."
            />

            {loading ? (
                <p className="crm-kyc-ob-loading">Loading KYC cases…</p>
            ) : cases.length === 0 ? (
                <div className="crm-empty-state crm-kyc-ob-empty">
                    <h3>No KYC onboarding case yet</h3>
                    <p>
                        Invite this partner from the KYC Dashboard — they will receive a
                        secure portal link to upload documents and complete the onboarding form.
                    </p>
                </div>
            ) : (
                <table className="crm-kyc-ob-table">
                    <thead>
                        <tr>
                            <th>Year</th>
                            <th>Form</th>
                            <th>Status</th>
                            <th>Documents</th>
                            <th>Submitted</th>
                            <th>Reviewed</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {cases.map((kycCase) => {
                            const meta = STATUS_META[kycCase.status] || STATUS_META.invited;
                            return (
                                <tr
                                    key={kycCase.id}
                                    onClick={() => history.push(`/dashboard/crm/kyc/case/${kycCase.id}`)}
                                    title="Open the full KYC case review"
                                >
                                    <td className="crm-kyc-ob-year">{kycCase.year}</td>
                                    <td>{kycCase.form_type === 'SA' ? 'KYC South Africa' : 'Standard'}</td>
                                    <td>
                                        <span className={`crm-status-badge ${meta.badge}`}>{meta.label}</span>
                                        {kycCase.review_due && (
                                            <span className="crm-status-badge crm-status-flagged crm-kyc-ob-due">
                                                Review due
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {kycCase.required_documents_uploaded}/{kycCase.required_documents_total} required
                                    </td>
                                    <td>
                                        {kycCase.submitted_at
                                            ? new Date(kycCase.submitted_at).toLocaleDateString()
                                            : '—'}
                                    </td>
                                    <td>
                                        {kycCase.reviewed_at
                                            ? new Date(kycCase.reviewed_at).toLocaleDateString()
                                            : '—'}
                                    </td>
                                    <td className="crm-kyc-ob-open">Open →</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default CRMKYCOnboardingSection;
